/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dispatch } from 'react';
import { CreatePolygonDTO } from './dto/create-polygon.dto';
import { EditLogicalReaderDTO } from './dto/edit-logical-reader.dto';
import { EditGroupTypeDTO } from './dto/edit-group-type.dto';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import { FacilityActionType } from './facility.enum';
import {
  IGetFacilitiesResponse,
  IGetZonesResponse,
  IGetGroupTypesResponse,
  IGetGroupsResponse,
  IGroupsTreeResponse,
  IGroupsTree,
  ICreateGroup,
  IEditGroup,
  IDeleteGroup,
  ICreateZone,
  ICreateProperty,
  ICreateGroupType,
  IEditGroupType,
  ILogicalReader,
  IPolygon,
  IGetMapOriginResponse,
  IGetMapImageResponse,
  IEditMapOrigin,
  IEditImage,
} from './i-facility';
import { facilityBaseUrl } from './facility.constants';
import { CreateGroupDTO } from './dto/create-group.dto';
import { CreateZoneDTO } from './dto/create-zone.dto';
import { CreatePropertyDTO } from './dto/create-property.dto';
import { EditGroupDTO } from './dto/edit-group.dto';
import { ICreateNewEntityResponse } from './dto/create-entity.dto';
import { CreateGroupTypeDTO } from './dto/create-group-type.dto';
import createHistory from '../../../utils/create-history';
import { CreateLogicalReaderDTO } from './dto/create-logical-reader.dto';
import { EditPolygonDTO } from './dto/edit-polygon.dto';
import { EditMapOriginDTO } from './dto/edit-map-origin.dto';
import { EditMapImageDTO } from './dto/edit-map-image.dto';

export interface IFacilityActions {
  getZones(id: string, limit?: number, skip?: number): Promise<void>;
  getFacilities(): Promise<void>;
  getGroupTypes(): Promise<void>;
  getGroups(): Promise<void>;
  getGroupsTree(): Promise<void>;
  setCurrentGroup(group: IGroupsTree): Promise<void>;
  createGroup(
    data: ICreateGroup,
  ): Promise<void>;
  editGroup(
    data: IEditGroup,
    mapOrigin: IEditMapOrigin,
    image: IEditImage,
    imageimage: File | any,
  ): Promise<void>;
  deleteGroup(data: IDeleteGroup): Promise<void>;
  createZone(data: ICreateZone, newFieldsTable : ILogicalReader[],
    polygons?:IPolygon[]): Promise<void>;
  editZone(data: ICreateZone): Promise<void>;
  deleteZone(propertiesId: number[], id: string): Promise<void>;
  createGroupType(data: ICreateGroupType): Promise<void>;
  editGroupType(data: IEditGroupType): Promise<void>;
  deleteGroupType(id:number): Promise<void>;
  editZoneProperty(data: ICreateProperty): Promise<void>;
  createZoneProperty(data: ICreateProperty): Promise<void>;
  deleteZoneProperty(id: number): Promise<void>
  getLogicalReaders(): Promise<void>;
  deleteLogicalReader(zone_id: number, id: number): Promise<void>;
  editLogicalReader(zone_id: number, id: number, data:
  { name: string, direction?:string | null }): Promise<void>;
  createLogicalReader(data: { name: string, zone_id: number,
    direction?:string | null }): Promise<void>;
  editPolygon(
    data:{ points:string },
    id?: number,
    zone_id?: number,) : Promise<void>;
  deletePolygon(zone_id: number, id: number): Promise<void>;
  createPolygon(data: IPolygon) : Promise<void>;
  getMapOrigin(id: string | null): Promise<void>;
  getMapImage(id: number | undefined | string): Promise<void>;
  getMapImageImage(id: number | undefined): Promise<void>;
  resetMapValues(): Promise<void>;
  resetMapReportValues(): Promise<void>;
}

export class FacilityActions implements IFacilityActions {
  protected readonly dispatch: Dispatch<IAction>;

  private readonly apiService: IApiService;

  private readonly state: IState;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
  ) {
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.state = state;
  }

  public getFacilities = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_FACILITIES_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetFacilitiesResponse>({ url: `${facilityBaseUrl}facilities` });
      this.dispatch({ type: FacilityActionType.GET_FACILITIES_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_FACILITIES_LIST_FAIL, payload: err });
    }
  };

  public getZones = async (id: string, limit = 0, skip = 0): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_ZONES_LIST_REQUEST, payload: null });
      const params = limit && skip ? { group_id: id, skip, limit } : { group_id: id };
      const payload = await this.apiService.get<IGetZonesResponse>({
        url: `${facilityBaseUrl}zones`,
        params,
      });

      const zonesNew = await this.apiService.get<any>({
        url: `${facilityBaseUrl}zoneTree`,
        params: {
          group_id: id,
        },
      });

      this.getLogicalReaders();

      this.getPolygons();

      this.dispatch({
        type: FacilityActionType.GET_ZONES_LIST_SUCCESS,
        payload:
          { data: payload, groupId: id },
      });

      this.dispatch({
        type: FacilityActionType.GET_ZONES_LIST_SUCCESS_NEW,
        payload: zonesNew,
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_ZONES_LIST_FAIL, payload: err });
    }
  };

  public getGroupTypes = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_GROUP_TYPES_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetGroupTypesResponse>({ url: `${facilityBaseUrl}groupTypes` });
      this.dispatch({ type: FacilityActionType.GET_GROUP_TYPES_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_GROUP_TYPES_LIST_FAIL, payload: err });
    }
  };

  public getGroups = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_GROUPS_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetGroupsResponse>({ url: `${facilityBaseUrl}groups` });
      this.dispatch({ type: FacilityActionType.GET_GROUPS_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_GROUPS_LIST_FAIL, payload: err });
    }
  };

  public getGroupsTree = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_GROUPS_TREE_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IGroupsTreeResponse>({
        url: `${facilityBaseUrl}groupTree`,
        params: {
          depth: 7,
        },
      });
      this.dispatch({ type: FacilityActionType.GET_GROUPS_TREE_LIST_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_GROUPS_TREE_LIST_FAIL, payload: err });
    }
  };

  public resetMapValues = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.RESET_MAP_IMAGE_VALUES, payload: null });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.RESET_MAP_IMAGE_VALUES_FAIL, payload: err });
    }
  };

  public resetMapReportValues = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.RESET_MAP_REPORT_VALUES, payload: null });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  };

  public setCurrentGroup = async (group: IGroupsTree): Promise<void> => {
    this.dispatch({ type: FacilityActionType.SET_CURRENT_GROUP_SUCCESS, payload: group });
  };

  public createGroup = async (
    {
      groupType_id,
      group_id,
      name,
      code,
    }: ICreateGroup,
  ): Promise<void> => {
    try {
      this.dispatch(
        {
          type: FacilityActionType.CREATE_GROUP_REQUEST,
          payload: {
            groupType_id,
            group_id,
            name,
            code,
          },
        },
      );
      const payload = await this.apiService.post<CreateGroupDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}groups`,
        data: {
          groupType_id,
          group_id: null,
          name,
          code,
        },
      });
      // this.dispatch({type:FacilityActionType.GET_GROUPS_TREE_LIST_REQUEST})
      this.getGroupsTree();
      createHistory.goBack();
      this.dispatch({ type: FacilityActionType.CREATE_GROUP_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.CREATE_GROUP_FAIL, payload: err });
    }
  };

  public editGroup = async ({ name, code, id }: IEditGroup,
    {
      group_id,
      longitude,
      latitude,
      elevation,
      azimuth,
      units,
    }: IEditMapOrigin,
    {
      mapImageId,
      mapOrigin_id,
      xofffset,
      yoffset,
      theta,
      pixelsPerLength,
    }: IEditImage,
    imageimage: File | any): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_GROUP_REQUEST, payload: null });
      const payload = await this.apiService.put<EditGroupDTO, void>({
        url: `${facilityBaseUrl}group/${id}`,
        data: {
          name,
          code,
        },
      });
      const mapOriginHasValues = longitude || latitude || elevation || azimuth || units;
      if (mapOriginHasValues) {
        const data = {
          group_id: group_id && parseInt(group_id, 10),
          name,
          longitude,
          latitude,
          elevation,
          azimuth,
          units,
        };
        let editMapOriginId = mapOrigin_id;
        if (!mapOrigin_id) {
          const r = await this.apiService.post<EditMapOriginDTO, any>({
            url: `${facilityBaseUrl}mapOrigins`,
            data,
          });
          editMapOriginId = r.id;
        } else {
          await this.apiService.put<EditMapOriginDTO, void>({
            url: `${facilityBaseUrl}mapOrigin/${mapOrigin_id}`,
            data,
          });
        }
        const imageData = {
          mapOrigin_id: editMapOriginId,
          xofffset,
          yoffset,
          theta,
          pixelsPerLength,
        };
        let editImageId = mapImageId;
        if (!mapImageId || mapImageId === 0) {
          const ir = await this.apiService.post<EditMapImageDTO, any>({
            url: `${facilityBaseUrl}mapImages`,
            data: imageData,
          });
          editImageId = ir.id;
        } else {
          await this.apiService.put<EditMapImageDTO, void>({
            url: `${facilityBaseUrl}mapImage/${editImageId}`,
            data: imageData,
          });
        }
        if (imageimage.imageimage) {
          await this.apiService.put({
            url: `${facilityBaseUrl}mapImageImage/${editImageId}`,
            headers: {
              'Content-Type': 'image/png',
            },
            data: imageimage.imageimage,
          });
        }
      }

      this.getGroups();
      this.getGroupsTree();
      this.dispatch({
        type: FacilityActionType.EDIT_GROUP_SUCCESS,
        payload,
      });
      window.location.reload();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_GROUP_FAIL, payload: err });
    }
  };

  public deleteGroup = async ({ id }: IDeleteGroup): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_GROUP_REQUEST, payload: null });
      const payload = await this.apiService.delete<EditGroupDTO>({
        url: `${facilityBaseUrl}group/${id}`,
      });

      this.getGroupsTree();
      this.dispatch({
        type: FacilityActionType.DELETE_GROUP_SUCCESS,
        payload,
      });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_GROUP_FAIL, payload: err });
    }
  };

  public createZoneProperty = async (data: ICreateProperty): Promise<void> => {
    this.dispatch({ type: FacilityActionType.CREATE_ZONE_PROPERTY_REQUEST, payload: null });
    await this.apiService.post<CreatePropertyDTO, ICreateNewEntityResponse>({
      url: `${facilityBaseUrl}properties`,
      data,
    });
  };

  public editZoneProperty = async (data: ICreateProperty): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_PROPERTY_REQUEST, payload: null });
      const payload = await this.apiService.put<CreatePropertyDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}property/${data.id}`,
        data,
      });
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_PROPERTY_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_PROPERTY_FAIL, payload: err });
    }
  };

  public deleteZoneProperty = async (id: number): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_PROPERTY_REQUEST, payload: null });
      const payload = await this.apiService.delete({
        url: `${facilityBaseUrl}property/${id}`,
      });
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_PROPERTY_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_PROPERTY_FAIL, payload: err });
    }
  };

  public createZone =
  async ({ group_id, name: zoneName, properties }: ICreateZone,
    newFieldsTable : ILogicalReader[], polygons:IPolygon[]): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.CREATE_ZONE_REQUEST, payload: null });
      const zone = await this.apiService.post<CreateZoneDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}zones`,
        data: {
          group_id,
          name: zoneName,
        },
      });

      if (properties?.length) {
        Promise.all(properties?.map(({ name, value }) => {
          if (name !== 'name') {
            return this.createZoneProperty(
              { name, value, zone_id: zone.id },
            );
          }
          return null;
        }));
      }

      if (newFieldsTable.length) {
        Promise.all(newFieldsTable.map(
          ({
            name,
            direction,
          }) => this.createLogicalReader({ name, zone_id: zone.id, direction }),
        ));
      }

      if (polygons.length) {
        Promise.all(polygons.map(({ points }) => {
          if (points) {
            return this.createPolygon({ zone_id: zone.id, points });
          }
          return null;
        }));
      }

      this.dispatch({ type: FacilityActionType.CREATE_ZONE_SUCCESS, payload: null });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.CREATE_ZONE_FAIL, payload: null });
    }
  };

  public editZone = async ({ group_id, name, id }: ICreateZone): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_REQUEST, payload: null });
      const payload = this.apiService.put<CreateZoneDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}zone/${id}`,
        data: {
          group_id,
          name,
        },
      });
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_SUCCESS, payload });
      // createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_ZONE_FAIL, payload: err });
    }
  };

  public deleteZone = async (propertiesId: number[], id: string): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_REQUEST, payload: null });
      if (propertiesId.length) {
        await Promise.all(propertiesId.map((property) => this.deleteZoneProperty(property)));
      }

      const payload = await this.apiService.delete<null>({
        url: `${facilityBaseUrl}zone/${id}`,
      });
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_SUCCESS, payload });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_ZONE_FAIL, payload: err });
    }
  };

  public createGroupType = async (data: ICreateGroupType): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.CREATE_GROUP_TYPE_REQUEST, payload: null });
      const payload = await this.apiService.post<CreateGroupTypeDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}groupTypes`,
        data,
      });

      this.dispatch({ type: FacilityActionType.CREATE_GROUP_TYPE_SUCCESS, payload });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.CREATE_GROUP_TYPE_FAIL, payload: null });
    }
  };

  public editGroupType = async ({ name, id, icon }: IEditGroupType): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_GROUP_TYPE_REQUEST, payload: null });
      const payload = await this.apiService.put<EditGroupTypeDTO, ICreateNewEntityResponse>({
        url: `${facilityBaseUrl}groupType/${id}`,
        data: {
          name,
          icon,
        },
      });
      this.dispatch({ type: FacilityActionType.EDIT_GROUP_TYPE_SUCCESS, payload });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_GROUP_TYPE_FAIL, payload: err });
    }
  };

  public deleteGroupType = async (id:number): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_GROUP_TYPE_REQUEST, payload: null });
      const payload = await this.apiService.delete<null>({
        url: `${facilityBaseUrl}groupType/${id}`,
      });

      this.dispatch({ type: FacilityActionType.DELETE_GROUP_TYPE_SUCCESS, payload });
      createHistory.goBack();
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_GROUP_TYPE_FAIL, payload: null });
    }
  };

  public getLogicalReaders = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_LOGICAL_READERS_REQUEST, payload: null });
      const payload = await this.apiService.get<ILogicalReader[]>({
        url: `${facilityBaseUrl}/logicalReaders`,
      });
      this.dispatch({ type: FacilityActionType.GET_LOGICAL_READERS_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_LOGICAL_READERS_FAIL, payload: err });
    }
  };

  public deleteLogicalReader = async (zone_id: number, id: number): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_LOGICAL_READER_REQUEST, payload: null });
      await this.apiService.delete<null>({
        url: `${facilityBaseUrl}logicalReader/${id}`,
      });
      this.dispatch({
        type: FacilityActionType.DELETE_LOGICAL_READER_SUCCESS,
        payload: { zone_id, id },
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_LOGICAL_READER_FAIL, payload: err });
    }
  };

  public editLogicalReader = async (zone_id: number,
    id: number,
    data:
    { name: string, direction?: string | null}): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_LOGICAL_READER_REQUEST, payload: null });
      await this.apiService.put<EditLogicalReaderDTO, null>({
        url: `${facilityBaseUrl}logicalReader/${id}`,
        data,
      });
      this.dispatch({
        type: FacilityActionType.EDIT_LOGICAL_READER_SUCCESS,
        payload: { zone_id, id, data },
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_LOGICAL_READER_FAIL, payload: err });
    }
  };

  public createLogicalReader = async (data: {
    name: string,
    zone_id: number,
    direction?:string | null
  }): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.CREATE_LOGICAL_READER_REQUEST, payload: null });
      await this.apiService.post<CreateLogicalReaderDTO, null>({
        url: `${facilityBaseUrl}logicalReaders`,
        data,
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.CREATE_LOGICAL_READER_FAIL, payload: err });
    }
  };

  public getPolygons = async (): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_POLYGONS_REQUEST, payload: null });
      const payload = await this.apiService.get<IPolygon[]>({
        url: `${facilityBaseUrl}/polygons`,
      });
      this.dispatch({ type: FacilityActionType.GET_POLYGONS_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_POLYGONS_FAIL, payload: err });
    }
  };

  public deletePolygon = async (zone_id: number, id: number): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.DELETE_POLYGON_REQUEST, payload: null });
      await this.apiService.delete<null>({
        url: `${facilityBaseUrl}polygon/${id}`,
      });
      this.dispatch({
        type: FacilityActionType.DELETE_POLYGON_SUCCESS,
        payload: { zone_id, id },
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.DELETE_POLYGON_FAIL, payload: err });
    }
  };

  public editPolygon = async (
    data:
    { points:string },
    id: number,
    zone_id?: number,
  ): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.EDIT_POLYGON_REQUEST, payload: null });
      await this.apiService.put<EditPolygonDTO, null>({
        url: `${facilityBaseUrl}polygon/${id}`,
        data,
      });
      this.dispatch({
        type: FacilityActionType.EDIT_POLYGON_SUCCESS,
        payload: { zone_id, id, data },
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.EDIT_POLYGON_FAIL, payload: err });
    }
  };

  public createPolygon = async (data: IPolygon)
  : Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.CREATE_POLYGON_REQUEST, payload: null });
      await this.apiService.post<CreatePolygonDTO, null>({
        url: `${facilityBaseUrl}polygons`,
        data,
      });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.CREATE_POLYGON_FAIL, payload: err });
    }
  };

  // Map Origin
  public getMapOrigin = async (id: string | null)
  : Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_MAPS_ORIGIN_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetMapOriginResponse>({
        url: `${facilityBaseUrl}mapOriginByFacility/${id}`, silent: true,
      });
      this.dispatch({ type: FacilityActionType.GET_MAPS_ORIGIN_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_MAPS_ORIGIN_FAIL, payload: err });
    }
  };

  // Map Image
  public getMapImage = async (id: number | undefined | string): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetMapImageResponse>({
        url: `${facilityBaseUrl}mapImageByFacility/${id}`, silent: true,
      });
      if (payload) {
        this.getMapImageImage(id);
      }
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_FAIL, payload: err });
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_IMAGE_FAIL, payload: err });
    }
  };

  // Map Image Image
  public getMapImageImage = async (id: number | undefined | string): Promise<void> => {
    try {
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_IMAGE_REQUEST, payload: null });
      const payload = await this.apiService.get<Blob>({
        url: `${facilityBaseUrl}mapImageImageByFacility/${id}`, responseType: 'blob', silent: true,
      });
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_IMAGE_SUCCESS, payload });
    } catch (err) {
      this.dispatch({ type: FacilityActionType.GET_MAPS_IMAGE_IMAGE_FAIL, payload: err });
    }
  };
}
